import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface User {
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  avatar?: string;
  profile_image?: string;
  is_online?: boolean;
  phone_verified?: boolean;
}

interface Member {
  id: string;
  user_id: string;
  role: string;
  status: string;
  joined_at: string;
  user?: User;
  total_contributions?: number;
  attendance_rate?: number;
  reputation_score?: number;
  savings_balance?: number;
  loan_balance?: number;
  last_contribution_date?: string;
  last_contribution_amount?: number;
  activity_summary?: {
    meetings_attended: number;
    total_meetings: number;
    contributions_made: number;
    loans_taken: number;
  };
  business_type?: string;
  location?: string;
}

interface Invitation {
  id: string;
  email: string;
  phone_number?: string;
  role: string;
  role_name?: string;
  status: string;
  created_at: string;
  expires_at: string;
  responded_at?: string;
  message?: string;
}

const ChamaMembers: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const colors = getThemeColors('dark');

  const [members, setMembers] = useState<Member[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'members' | 'invitations'>('members');
  const [sentInvitations, setSentInvitations] = useState<Invitation[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [memberStats, setMemberStats] = useState<any>({});
  const [userRole, setUserRole] = useState('member');

  const roles = [
    { id: 'chairperson', name: 'Chairperson', icon: 'star', description: 'Full administrative access' },
    { id: 'treasurer', name: 'Treasurer', icon: 'wallet', description: 'Manages finances' },
    { id: 'secretary', name: 'Secretary', icon: 'document-text', description: 'Keeps records' },
    { id: 'assistant', name: 'Assistant', icon: 'person-add', description: 'Helps with operations' },
    { id: 'member', name: 'Member', icon: 'person', description: 'Regular member' },
  ];

  useEffect(() => {
    if (chamaId) {
      loadMembers();
      if (canManageMembers()) {
        loadSentInvitations();
      }
    }
  }, [chamaId]);

  useEffect(() => {
    applyFilters();
  }, [members, searchQuery]);

  const loadMembers = async (silent = false) => {
    if (!chamaId) return;

    try {
      if (!silent) setLoading(true);

      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const response = await fetch(API_ENDPOINTS.CHAMA_MEMBERS(chamaId), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Handle different possible response structures
        const membersData = data.members || data.data || [];
        setMembers(membersData);
        setLastUpdated(new Date().toISOString());

        // Calculate member stats from actual data
        setMemberStats({
          total_members: membersData.length,
          active_members: membersData.filter((m: Member) => m.status === 'active').length,
          pending_members: membersData.filter((m: Member) => m.status === 'pending').length,
        });

        // Set user role from API response or local storage
        setUserRole(data.user_role || 'member');
      } else {
        // If success is false but we got a response, it might be a success message
        if (data.message && data.message.includes('retrieved successfully')) {
          // Assume empty members array for now
          setMembers([]);
          setMemberStats({
            total_members: 0,
            active_members: 0,
            pending_members: 0,
          });
          setUserRole('member');
        } else {
          throw new Error(data.message || 'Failed to load members');
        }
      }

    } catch (error) {
      console.error('Failed to load members:', error);
      if (!silent) {
        alert('Failed to load chama members. Please try again.');
      }
      // Set empty state on error
      setMembers([]);
      setMemberStats({
        total_members: 0,
        active_members: 0,
        pending_members: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSentInvitations = async (silent = false) => {
    if (!canManageMembers() || !chamaId) return;

    try {
      if (!silent) setInvitationsLoading(true);

      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const response = await fetch(`${API_ENDPOINTS.CHAMA_DETAILS(chamaId)}/invitations`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.invitations) {
        setSentInvitations(data.invitations);
      } else {
        throw new Error(data.message || 'Failed to load invitations');
      }

    } catch (error) {
      console.error('Failed to load sent invitations:', error);
      if (!silent) {
        alert('Failed to load invitations');
      }
      // Set empty state on error
      setSentInvitations([]);
    } finally {
      if (!silent) setInvitationsLoading(false);
    }
  };

  const onRefresh = async () => {
    if (!chamaId) return;

    setRefreshing(true);
    await Promise.all([
      loadMembers(true),
      activeTab === 'invitations' ? loadSentInvitations(true) : Promise.resolve()
    ]);
    setRefreshing(false);
  };

  const canManageMembers = () => {
    return ['chairperson', 'secretary', 'treasurer'].includes(userRole) && !!chamaId;
  };

  const canManageRoles = () => {
    return userRole === 'chairperson' && !!chamaId;
  };

  const applyFilters = () => {
    if (!searchQuery) {
      setFilteredMembers(members);
      return;
    }

    const filtered = members.filter(member =>
      `${member.user?.first_name} ${member.user?.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.user?.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredMembers(filtered);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getRoleIcon = (role: string): string => {
    const roleData = roles.find(r => r.id === role);
    return roleData?.icon || 'person';
  };

  const getRoleColor = (role: string): string => {
    switch (role) {
      case 'chairperson': return colors.warning;
      case 'treasurer': return colors.warning;
      case 'secretary': return colors.warning;
      case 'assistant': return colors.secondary;
      default: return colors.textSecondary;
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInvitationStatus = (invitation: Invitation) => {
    const now = new Date();
    const expiresAt = new Date(invitation.expires_at);

    if (invitation.status === 'accepted') return { status: 'accepted', color: colors.success };
    if (invitation.status === 'rejected') return { status: 'rejected', color: colors.error };
    if (invitation.status === 'cancelled') return { status: 'cancelled', color: colors.textSecondary };
    if (expiresAt < now) return { status: 'expired', color: colors.warning };
    return { status: 'pending', color: colors.primary };
  };

  const renderMemberCard = (member: Member) => {
    const user = member.user || {} as User;
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim() || 'Unknown Member';
    const email = user.email || '';
    const initials = (firstName[0] || '') + (lastName[0] || '');

    return (
      <div
        key={member.id}
        style={{
          backgroundColor: colors.surface,
          borderRadius: borderRadius.lg,
          padding: spacing.lg,
          marginBottom: spacing.md,
          ...getShadowStyle('md'),
          border: `1px solid ${colors.border}`,
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: spacing.md,
        }}>
          <div style={{ position: 'relative', marginRight: spacing.md }}>
            <div style={{
              width: 50,
              height: 50,
              borderRadius: 25,
              backgroundColor: colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              fontWeight: 'bold',
              color: colors.white,
            }}>
              {initials || '?'}
            </div>
            {user.is_online && (
              <div style={{
                position: 'absolute',
                bottom: 2,
                right: 2,
                width: 12,
                height: 12,
                borderRadius: 6,
                backgroundColor: colors.success,
                border: `2px solid ${colors.surface}`,
              }} />
            )}
          </div>

          <div style={{ flex: 1 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.xs,
              marginBottom: spacing.xs,
            }}>
              <span style={{
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
                color: colors.text,
              }}>
                {fullName}
              </span>
              {user.phone_verified && (
                <span style={{ color: colors.success, fontSize: 16 }}>✓</span>
              )}
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: spacing.xs,
            }}>
              <span style={{
                fontSize: 14,
                color: getRoleColor(member.role),
                marginRight: spacing.sm,
              }}>
                {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
              </span>
              <div style={{
                padding: `${spacing.xs} ${spacing.sm}`,
                borderRadius: borderRadius.sm,
                backgroundColor: member.status === 'active' ? colors.success + '20' : colors.warning + '20',
              }}>
                <span style={{
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.medium,
                  color: member.status === 'active' ? colors.success : colors.warning,
                  textTransform: 'capitalize',
                }}>
                  {member.status}
                </span>
              </div>
            </div>

            <div style={{ fontSize: typography.fontSize.sm, color: colors.textTertiary }}>
              {member.business_type || 'Member'} • {member.location || 'Location not set'}
            </div>
            <div style={{ fontSize: typography.fontSize.sm, color: colors.textTertiary }}>
              Joined {new Date(member.joined_at).toLocaleDateString()}
            </div>
          </div>

          {canManageMembers() && (
            <button style={{
              padding: spacing.sm,
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}>
              <span style={{ fontSize: 20, color: colors.textSecondary }}>⋯</span>
            </button>
          )}
        </div>

        {/* Stats Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: spacing.md,
          paddingTop: spacing.md,
          paddingBottom: spacing.md,
          borderTop: `1px solid ${colors.border}`,
          borderBottom: `1px solid ${colors.border}`,
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.textSecondary,
              marginBottom: spacing.xs,
            }}>
              Total Contributions
            </div>
            <div style={{
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              color: colors.success,
            }}>
              {formatCurrency(member.total_contributions || 0)}
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.textSecondary,
              marginBottom: spacing.xs,
            }}>
              Attendance Rate
            </div>
            <div style={{
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text,
            }}>
              {member.attendance_rate || 0}%
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.textSecondary,
              marginBottom: spacing.xs,
            }}>
              Reputation
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text,
            }}>
              <span style={{ color: colors.warning, marginRight: 4 }}>★</span>
              {member.reputation_score?.toFixed(1) || 0}
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div style={{
          marginBottom: spacing.md,
          paddingTop: spacing.md,
          paddingBottom: spacing.md,
          borderBottom: `1px solid ${colors.border}`,
          gap: spacing.sm,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{
              fontSize: typography.fontSize.sm,
              color: colors.textSecondary,
            }}>
              Savings Balance
            </span>
            <span style={{
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: colors.primary,
            }}>
              {formatCurrency(member.savings_balance || 0)}
            </span>
          </div>

          {member.loan_balance && member.loan_balance > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
              }}>
                Loan Balance
              </span>
              <span style={{
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                color: colors.error,
              }}>
                {formatCurrency(member.loan_balance)}
              </span>
            </div>
          )}

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{
              fontSize: typography.fontSize.sm,
              color: colors.textSecondary,
            }}>
              Last Contribution
            </span>
            <span style={{
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: colors.text,
            }}>
              {member.last_contribution_date
                ? `${formatCurrency(member.last_contribution_amount || 0)} on ${new Date(member.last_contribution_date).toLocaleDateString()}`
                : 'None'
              }
            </span>
          </div>
        </div>

        {/* Activity Summary */}
        {member.activity_summary && (
          <div style={{
            marginBottom: spacing.md,
            paddingBottom: spacing.md,
            borderBottom: `1px solid ${colors.border}`,
          }}>
            <div style={{
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text,
              marginBottom: spacing.md,
            }}>
              Recent Activity
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-around',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: typography.fontSize.lg,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.primary,
                  marginBottom: spacing.xs,
                }}>
                  {member.activity_summary.meetings_attended}/{member.activity_summary.total_meetings}
                </div>
                <div style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.textSecondary,
                }}>
                  Meetings
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: typography.fontSize.lg,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.success,
                  marginBottom: spacing.xs,
                }}>
                  {member.activity_summary.contributions_made}
                </div>
                <div style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.textSecondary,
                }}>
                  Contributions
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: typography.fontSize.lg,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.warning,
                  marginBottom: spacing.xs,
                }}>
                  {member.activity_summary.loans_taken}
                </div>
                <div style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.textSecondary,
                }}>
                  Loans
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: spacing.md,
        }}>
          <button style={{
            flex: 1,
            padding: spacing.sm,
            borderRadius: borderRadius.md,
            border: 'none',
            backgroundColor: member.user_id === 'user1' ? colors.primary : 'transparent',
            color: member.user_id === 'user1' ? colors.white : colors.primary,
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            cursor: 'pointer',
          }}>
            {member.user_id === 'user1' ? "View My Profile" : "View Details"}
          </button>

          {member.user_id !== 'user1' && (
            <button style={{
              flex: 1,
              padding: spacing.sm,
              borderRadius: borderRadius.md,
              border: 'none',
              backgroundColor: colors.success,
              color: colors.white,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              cursor: 'pointer',
            }}>
              Chat
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderInvitationCard = (invitation: Invitation) => {
    const statusInfo = getInvitationStatus(invitation);
    const isExpired = statusInfo.status === 'expired';
    const isPending = statusInfo.status === 'pending';

    return (
      <div
        key={invitation.id}
        style={{
          backgroundColor: colors.surface,
          borderRadius: borderRadius.lg,
          padding: spacing.lg,
          marginBottom: spacing.md,
          ...getShadowStyle('md'),
          border: `1px solid ${colors.border}`,
          opacity: isExpired ? 0.7 : 1,
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: spacing.md,
        }}>
          <div style={{ flex: 1, marginRight: spacing.md }}>
            <div style={{
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text,
              marginBottom: spacing.xs,
            }}>
              {invitation.email}
            </div>
            {invitation.phone_number && (
              <div style={{
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
                marginBottom: spacing.xs,
              }}>
                {invitation.phone_number}
              </div>
            )}
            {invitation.role && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: spacing.xs,
              }}>
                <span style={{ color: colors.primary, marginRight: spacing.xs }}>🛡️</span>
                <span style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.primary,
                }}>
                  {invitation.role_name || invitation.role}
                </span>
              </div>
            )}
          </div>

          <div style={{
            padding: `${spacing.xs} ${spacing.sm}`,
            borderRadius: borderRadius.sm,
            backgroundColor: statusInfo.color + '20',
          }}>
            <span style={{
              fontSize: typography.fontSize.xs,
              color: statusInfo.color,
              fontWeight: typography.fontWeight.medium,
              textTransform: 'uppercase',
            }}>
              {statusInfo.status}
            </span>
          </div>
        </div>

        <div style={{
          gap: spacing.sm,
          marginBottom: spacing.md,
          paddingTop: spacing.md,
          paddingBottom: spacing.md,
          borderTop: `1px solid ${colors.border}`,
          borderBottom: `1px solid ${colors.border}`,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm,
          }}>
            <span style={{ color: colors.textSecondary, fontSize: 14 }}>📅</span>
            <span style={{
              fontSize: typography.fontSize.sm,
              color: colors.textSecondary,
              minWidth: 80,
            }}>
              Sent:
            </span>
            <span style={{
              fontSize: typography.fontSize.sm,
              color: colors.text,
              flex: 1,
            }}>
              {formatDate(invitation.created_at)}
            </span>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm,
          }}>
            <span style={{ color: colors.textSecondary, fontSize: 14 }}>⏰</span>
            <span style={{
              fontSize: typography.fontSize.sm,
              color: colors.textSecondary,
              minWidth: 80,
            }}>
              Expires:
            </span>
            <span style={{
              fontSize: typography.fontSize.sm,
              color: isExpired ? colors.error : colors.text,
              flex: 1,
            }}>
              {formatDate(invitation.expires_at)}
            </span>
          </div>

          {invitation.responded_at && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
            }}>
              <span style={{ color: statusInfo.color, fontSize: 14 }}>✓</span>
              <span style={{
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
                minWidth: 80,
              }}>
                Responded:
              </span>
              <span style={{
                fontSize: typography.fontSize.sm,
                color: colors.text,
                flex: 1,
              }}>
                {formatDate(invitation.responded_at)}
              </span>
            </div>
          )}
        </div>

        {invitation.message && (
          <div style={{
            marginBottom: spacing.md,
            padding: spacing.md,
            backgroundColor: colors.backgroundSecondary,
            borderRadius: borderRadius.md,
          }}>
            <div style={{
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: colors.textSecondary,
              marginBottom: spacing.xs,
            }}>
              Message:
            </div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text,
              lineHeight: '20px',
            }}>
              {invitation.message}
            </div>
          </div>
        )}

        {isPending && (
          <div style={{
            display: 'flex',
            gap: spacing.md,
          }}>
            <button style={{
              flex: 1,
              padding: `${spacing.sm} ${spacing.md}`,
              borderRadius: borderRadius.md,
              border: `1px solid ${colors.border}`,
              backgroundColor: 'transparent',
              color: colors.primary,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              cursor: 'pointer',
            }}>
              Resend
            </button>
            <button style={{
              flex: 1,
              padding: `${spacing.sm} ${spacing.md}`,
              borderRadius: borderRadius.md,
              border: `1px solid ${colors.error}`,
              backgroundColor: 'transparent',
              color: colors.error,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              cursor: 'pointer',
            }}>
              Cancel
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderEmptyState = () => (
    <div style={{
      textAlign: 'center',
      padding: spacing.xxxl,
    }}>
      <div style={{
        fontSize: 64,
        color: colors.textTertiary,
        marginBottom: spacing.lg,
      }}>
        {activeTab === 'members' ? '👥' : '📧'}
      </div>
      <div style={{
        fontSize: typography.fontSize.xl,
        fontWeight: typography.fontWeight.semibold,
        color: colors.text,
        marginBottom: spacing.sm,
      }}>
        {activeTab === 'members' ? 'No members found' : 'No invitations sent'}
      </div>
      <div style={{
        fontSize: typography.fontSize.base,
        color: colors.textSecondary,
      }}>
        {activeTab === 'members'
          ? (searchQuery ? 'Try adjusting your search' : 'Invite people to join your chama')
          : 'Send invitations to grow your chama membership'
        }
      </div>
    </div>
  );

  const renderTabNavigation = () => (
    <div style={{
      display: 'flex',
      borderBottom: `1px solid ${colors.border}`,
    }}>
      <button
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: `${spacing.md} ${spacing.sm}`,
          borderBottom: activeTab === 'members' ? `2px solid ${colors.primary}` : '2px solid transparent',
          backgroundColor: activeTab === 'members' ? colors.primary + '15' : 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
        onClick={() => setActiveTab('members')}
      >
        <span style={{ marginRight: spacing.xs }}>👥</span>
        <span style={{
          fontSize: typography.fontSize.sm,
          fontWeight: typography.fontWeight.medium,
          color: activeTab === 'members' ? colors.primary : colors.textSecondary,
        }}>
          Members ({filteredMembers.length})
        </span>
      </button>

      {canManageMembers() && (
        <button
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: `${spacing.md} ${spacing.sm}`,
            borderBottom: activeTab === 'invitations' ? `2px solid ${colors.primary}` : '2px solid transparent',
            backgroundColor: activeTab === 'invitations' ? colors.primary + '15' : 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
          onClick={() => setActiveTab('invitations')}
        >
          <span style={{ marginRight: spacing.xs }}>📧</span>
          <span style={{
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            color: activeTab === 'invitations' ? colors.primary : colors.textSecondary,
          }}>
            Invitations ({sentInvitations.length})
          </span>
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: colors.background,
        padding: spacing.md,
      }}>
        <div style={{
          textAlign: 'center',
          padding: spacing.xxxl,
        }}>
          <div style={{
            fontSize: 48,
            color: colors.textTertiary,
            marginBottom: spacing.lg,
          }}>
            ⏳
          </div>
          <div style={{
            fontSize: typography.fontSize.xl,
            fontWeight: typography.fontWeight.semibold,
            color: colors.text,
          }}>
            Loading members...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Chama Members"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
        {/* Header */}
        <div style={{
          backgroundColor: colors.surface,
          padding: spacing.md,
          borderRadius: borderRadius.lg,
          marginBottom: spacing.lg,
          ...getShadowStyle('md'),
          border: `1px solid ${colors.border}`,
        }}>
          <input
            type="text"
            placeholder={activeTab === 'members' ? "Search members..." : "Search invitations..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: spacing.md,
              borderRadius: borderRadius.md,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.surface,
              color: colors.text,
              fontSize: typography.fontSize.base,
              outline: 'none',
            }}
          />

          {/* Stats Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: spacing.sm,
          }}>
            <div>
              <div style={{
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                color: colors.textSecondary,
              }}>
                {activeTab === 'members'
                  ? `${filteredMembers.length} member${filteredMembers.length !== 1 ? 's' : ''}`
                  : `${sentInvitations.length} invitation${sentInvitations.length !== 1 ? 's' : ''}`
                }
              </div>
              {activeTab === 'members' && memberStats.total_members && (
                <div style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.textTertiary,
                  marginTop: spacing.xs,
                }}>
                  {memberStats.active_members} active • {memberStats.pending_members} pending
                </div>
              )}
              {activeTab === 'invitations' && sentInvitations.length > 0 && (
                <div style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.textTertiary,
                  marginTop: spacing.xs,
                }}>
                  {sentInvitations.filter(inv => inv.status === 'pending').length} pending • {' '}
                  {sentInvitations.filter(inv => inv.status === 'accepted').length} accepted • {' '}
                  {sentInvitations.filter(inv => new Date(inv.expires_at) < new Date()).length} expired
                </div>
              )}
            </div>

            {lastUpdated && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.xs,
              }}>
                <span style={{ fontSize: 12, color: colors.textTertiary }}>🕒</span>
                <span style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.textTertiary,
                }}>
                  Updated {new Date(lastUpdated).toLocaleTimeString()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        {renderTabNavigation()}

        {/* Content */}
        <div style={{ padding: spacing.md }}>
          {activeTab === 'members' ? (
            filteredMembers.length > 0 ? (
              filteredMembers.map(renderMemberCard)
            ) : (
              renderEmptyState()
            )
          ) : (
            sentInvitations.length > 0 ? (
              sentInvitations.map(renderInvitationCard)
            ) : (
              renderEmptyState()
            )
          )}
        </div>

        {/* FAB */}
        {canManageMembers() && (
          <button style={{
            position: 'fixed',
            bottom: spacing.xl,
            right: spacing.xl,
            width: 56,
            height: 56,
            borderRadius: 28,
            backgroundColor: colors.primary,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: 1000,
          }}>
            <span style={{ fontSize: 24, color: colors.white }}>➕</span>
          </button>
        )}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80, // Space for tab bar
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80, // Space for fixed header
    paddingBottom: 80, // Space for fixed tab bar
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
};

export default ChamaMembers;